<template>
  <div>
    <template v-if="readOnly">
      <div :class="{ 'text-right w-full': singleLine }" class="p-2">
        <template v-if="modelValue !== null && modelValue !== undefined && userPreferences">
          <FormatNumber :decimal-sep="userPreferences.numberFormatDecimalSeparator"
                        :precision="props.header.numberFormatDecimalPrecision"
                        :thousand-sep="getThousandsSeparator(userPreferences)"
                        :use-precision="usePrecision"
                        :value="modelValue" />
        </template>
        <template v-else>
          <span class="italic text-fg-muted">(blank)</span>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="p-0 w-full">
        <div class="relative flex items-center w-full">
          <BaseInput :id="id" :clearable="header.isNullable" :error="inputError" :model-value="formattedModelValue"
                     class="w-full" type="text" @change="changed" />
        </div>
      </div>
      <BaseProgressBar v-if="saving" />
      <div v-else class="h-1 m-0 p-0" />
    </template>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useTableStore } from '@/store/storeTable';
import FormatNumber from '@/components/singleComponents/FormatNumber.vue';
import { useUserPreferencesStore } from '@/store/storeUserPreferences.js';
import { formatSafe } from '@/helpers/NumberFormatLib.js';
import BaseInput from '@/components/BaseComponents/BaseInput.vue';
import BaseProgressBar from '@/components/BaseComponents/BaseProgressBar.vue';
import { useValidationRulesStore } from '@/store/validationRulesStore'
import { useCellSave } from '@/composables/useCellSave'

const userPreferencesStore = useUserPreferencesStore();
const { userPreferences } = storeToRefs(useUserPreferencesStore());
const validationRulesStore = useValidationRulesStore();

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  singleLine: {
    type: Boolean,
    default: false,
  },
  autoSave: {
    type: Boolean,
    default: true,
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true },
  onSaveSuccess: { type: Function, default: null },
  // US 3399: valgfri id som settes paa selve kontrollen, saa en etikett kan knyttes med for=.
  // Uten den erklaeringen ville Vues attribute-fallthrough lagt id-en paa rot-diven, og en
  // for=-peker dit navngir ingenting for en skjermleser selv om statisk analyse blir fornoeyd.
  id: { type: String, default: undefined }
});

const emit = defineEmits(['update:modelValue']);
// Lokal valideringstilstand (format/min/max/tom-verdi); lagringsfeil eies av useCellSave.
const hasError = ref(false);
const errorMessage = ref('');
const { saving, saveErrorMessage, saveCell } = useCellSave();

const inputError = computed(() => {
  if (hasError.value) return errorMessage.value.trim() || 'Feil';
  return saveErrorMessage.value;
});

const tableStore = useTableStore();

// Visning satt eksplisitt av changed() (rå input ved valideringsfeil, formatert verdi etter
// lagring). Nøkles til modelValue-verdien den ble satt for: endres prop-en utenfra, faller
// visningen tilbake til formatert modelValue.
const displayOverride = ref(null);
const minValue = ref(null);
const maxValue = ref(null);

const usePrecision = computed(() => {
  const t = props.header.primaryFieldType;
  return t !== 'Integer' && t !== 'BigInt';
});

const getThousandsSeparator = () => {
  return props.header.numberFormatShowThousandsSeparator
    ?
    userPreferences.value.numberFormatThousandsSeparator
    :
    '';
};

const formatInputValue = (newValue) => {
  if (newValue === '' || newValue === null || newValue === undefined) {
    return '';
  }

  // Sjekk at userPreferences er lastet
  if (!userPreferences.value || !userPreferences.value.numberFormatDecimalSeparator) {
    // Hvis preferences ikke er lastet, returner råverdi som string
    return String(newValue);
  }

  // formatSafe faller tilbake til råverdi som string hvis formateringen feiler.
  return formatSafe(newValue, {
    thousandSep: getThousandsSeparator(),
    decimalSep: userPreferences.value.numberFormatDecimalSeparator,
    usePrecision: usePrecision.value,
    precision: props.header.numberFormatDecimalPrecision,
  });
};

// Avledet visningsverdi: formatert modelValue, med mindre changed() har satt en override
// for nettopp denne modelValue-verdien. Reagerer reaktivt på både prop-endringer og at
// userPreferences blir lastet.
const formattedModelValue = computed(() => {
  const override = displayOverride.value;
  if (override !== null && override.forValue === props.modelValue) {
    return override.text;
  }
  return formatInputValue(props.modelValue);
});

onMounted(async () => {
  // Sørg for at userPreferences er lastet, slik at formatert visning kan beregnes
  await userPreferencesStore.getUserPreferences();

  if (props.header.field) {
    const tableKey = String(tableStore.tableKey || '').trim();
    if (tableKey) {
      await validationRulesStore.setTableKey(tableKey);
    }
    minValue.value = validationRulesStore.getMinValueForColumn(props.header.field)
    maxValue.value = validationRulesStore.getMaxValueForColumn(props.header.field)
  }
});

// Handle an empty/null submitted value: clear-and-save when the column is nullable, otherwise
// raise the required-field error. Mirrors the original inline branch verbatim.
async function handleEmptyValue () {
  if (props.header.isNullable) {
    emit('update:modelValue', null);
    if (!props.autoSave) return;
    const item = {
      tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
      rowId: props.rowId,
      columnName: props.header.field,
      oldValue: props.modelValue,
      newValue: null
    };
    hasError.value = false;
    errorMessage.value = '';
    await saveCell(item, props.onSaveSuccess);
    return;
  } else {
    hasError.value = true;
    errorMessage.value = 'Feltet kan ikke være tomt';
    return;
  }
}

async function changed (newValue) {
  if (newValue === '' || newValue === null) {
    await handleEmptyValue();
    return;
  }
  let parsedValue = newValue.trim().replaceAll(' ', '');
  parsedValue = parsedValue.replaceAll(userPreferences.value.numberFormatThousandsSeparator, '');
  parsedValue = parsedValue.replaceAll(userPreferences.value.numberFormatDecimalSeparator, '.');

  if (!usePrecision.value && parsedValue.indexOf('.') !== -1) {
    parsedValue = parsedValue.slice(0, parsedValue.lastIndexOf('.'));
  }

  if (!isNumeric(parsedValue)) {
    hasError.value = true;
    errorMessage.value = `Ugyldig tallformat. Bruk kun tall sammen med tusenskilletegn '${userPreferences.value.numberFormatThousandsSeparator}'
     og desimaltegn '${userPreferences.value.numberFormatDecimalSeparator}'.`;
    console.error('Invalid numeric value', newValue, parsedValue);
    displayOverride.value = { forValue: props.modelValue, text: newValue };
    return;
  }

  const numValue = Number.parseFloat(parsedValue);

  if (minValue.value !== null && numValue < minValue.value) {
    hasError.value = true;
    errorMessage.value = `Verdien må være minst ${minValue.value}`;
    displayOverride.value = { forValue: props.modelValue, text: newValue };
    return;
  }

  if (maxValue.value !== null && numValue > maxValue.value) {
    hasError.value = true;
    errorMessage.value = `Verdien må være maksimalt ${maxValue.value}`;
    displayOverride.value = { forValue: props.modelValue, text: newValue };
    return;
  }

  emit('update:modelValue', parsedValue);

  if (!props.autoSave) return;

  const item = {
    tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
    rowId: props.rowId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue: parsedValue,
  };

  hasError.value = false;
  errorMessage.value = '';
  const savedWithoutError = await saveCell(item, props.onSaveSuccess);
  if (savedWithoutError) {
    displayOverride.value = { forValue: props.modelValue, text: formatInputValue(parsedValue) };
  }
}

function isNumeric (str) {
  if (typeof str != 'string') return false; // we only process strings!
  return !Number.isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !Number.isNaN(Number.parseFloat(str)); // ...and ensure strings of whitespace fail
}

</script>
